import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import trashIcon from '@assets/images/red-trash-icon.svg'

import { LocalFile, RemoteFile, Zone } from '@types'

import { useModal } from '@hooks/app/modal.hook'

import { Modal } from '@components/Modal'
import { Input } from '@components/Input'
import { TextArea } from '@components/TextArea'
import { Refresh } from '@components/Refresh'
import { PopUpDelete } from '@components/PopUpDelete'

import { useZone } from '@hooks/zone/zone.hook'
import { useUpdateZone } from '@hooks/zone/update-zone.hook'
import { useDeleteZone } from '@hooks/zone/delete-zone.hook'

import { Container, DeleteButton, DeleteButtonIcon, DeleteButtonLabel, FooterSeparator } from './edit-floor.styled'
import { getFileUrl } from '@utils/getFileUrl'
import { UploadFile } from '@components/UploadFile'

export const EditFloorPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const floorId = searchParams.get('floorId')

  const { projectId } = useParams()

  const { data, loading, error, refetch } = useZone(floorId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const floor = data?.zone

  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [plans, setPlans] = useState<(LocalFile | RemoteFile)[]>([])

  const loadPlanPreview = useCallback(async () => {
    if (floor && floor.plans[0]?.url) {
      const plan = floor.plans[0]
      const url = await getFileUrl(plan.url)

      const remoteFile: RemoteFile = {
        id: plan.id,
        deleted: false,
        name: plan.file.name,
        type: plan.file.type,
        extension: plan.file.extension,
        url,
      }
      setPlans([remoteFile])
    }
  }, [floor])

  const { updateZone } = useUpdateZone()

  useEffect(() => {
    if (floor) {
      setName(floor.name)
      setDescription(floor.description)

      loadPlanPreview()
    }
  }, [floor, loadPlanPreview])

  const { deleteZone } = useDeleteZone()

  const [deletingFloor, setDeletingFloor] = useState<Zone | undefined>(undefined)
  const [deletingFile, setDeletingFile] = useState<RemoteFile | undefined>(undefined)

  return (
    <Modal
      title={t('modules_project-settings_pages_edit_floor_title')}
      onSubmitLabel={t('modules_project-settings_pages_edit_floor_button_edit-floor')}
      onClose={() => {
        hideModal('update_floor', { floorId })
      }}
      onSubmit={() => {
        if (floor && projectId)
          updateZone(
            {
              id: floor.id,
              name,
              description,
            },
            plans,
            projectId,
          )

        hideModal('update_floor', { floorId })
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(floor?.id || '')}>
          {floor && (
            <>
              <Input
                autoFocus
                required
                label={t('modules_project-settings_pages_edit_floor_label_floor-name')}
                placeholder={t('modules_project-settings_pages_edit_floor_placeholder_floor-name')}
                value={name}
                onChange={(v) => setName(v)}
              />
              <TextArea
                label={t('modules_project-settings_pages_edit_floor_label_description')}
                placeholder={t('modules_project-settings_pages_edit_floor_placeholder_description')}
                value={description}
                onChange={(v) => setDescription(v)}
              />
              <UploadFile
                label={t('modules_project-settings_pages_manage_zones_label_zone_plans')}
                labelUpload={t('modules_project-settings_pages_manage_zones_label_upload_plans')}
                files={plans}
                setFiles={(_plans) => {
                  const remoteFileDeleted = _plans.find(
                    (plan) => Object.hasOwn(plan, 'deleted') && (plan as RemoteFile).deleted,
                  ) as RemoteFile | undefined

                  if (_plans.length == 1 && remoteFileDeleted) {
                    setDeletingFile(remoteFileDeleted)
                  } else {
                    setPlans([..._plans])
                  }
                }}
              />

              <FooterSeparator />

              <DeleteButton onClick={() => setDeletingFloor(floor)}>
                <DeleteButtonIcon src={trashIcon} />
                <DeleteButtonLabel>{t('modules_project-settings_pages_zoning_delete_floor_title')}</DeleteButtonLabel>
              </DeleteButton>
            </>
          )}
        </Refresh>
      </Container>

      {deletingFloor && (
        <PopUpDelete
          title={t('modules_project-settings_pages_zoning_delete_floor_title')}
          description={t('modules_project-settings_pages_zoning_delete_floor_descripition', {
            name: deletingFloor.name,
          })}
          onSubmit={() => {
            deleteZone(deletingFloor.id)

            setDeletingFloor(undefined)

            hideModal('update_floor', { floorId })
          }}
          onClose={() => setDeletingFloor(undefined)}
        />
      )}

      {deletingFile && (
        <PopUpDelete
          title={t('modules_project-settings_pages_zoning_delete_plan_title')}
          description={t('modules_project-settings_pages_zoning_delete_plan_descripition', {
            name: deletingFile.name,
          })}
          onSubmit={() => {
            setPlans([deletingFile] as (RemoteFile | LocalFile)[])
            setDeletingFile(undefined)
          }}
          onClose={() => setDeletingFile(undefined)}
        />
      )}
    </Modal>
  )
}
